import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Upload, X, File } from "lucide-react";
import { BlobStore } from "@/stores/BlobStore";
import { formatFileSize } from "@/utils/formatters";

interface UploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UploadDialog({ open, onOpenChange }: UploadDialogProps) {
  const { t } = useTranslation();
  const uploadBlobs = BlobStore(state => state.uploadBlobs);
  const uploading = BlobStore(state => state.uploading);
  const blobs = BlobStore(state => state.blobs);

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Helper function to check if a file already exists in blob storage
  const checkExistingFiles = (files: File[]) => {
    const existingBlobNames = new Set(blobs.map(blob => blob.name));
    return files.filter(file => existingBlobNames.has(file.name));
  };

  // Helper function to process files with overwrite confirmation
  const processFilesWithConfirmation = async (newFiles: File[]) => {
    const existingFiles = checkExistingFiles(newFiles);
    const filesToAdd: File[] = [];

    // First, add all non-existing files
    const nonExistingFiles = newFiles.filter(file => !existingFiles.includes(file));
    filesToAdd.push(...nonExistingFiles);

    // Then, ask for confirmation for each existing file individually
    for (const existingFile of existingFiles) {
      const message = `The file "${existingFile.name}" already exists in storage. Do you want to overwrite it?`;
      const confirmed = window.confirm(message);

      if (confirmed) {
        filesToAdd.push(existingFile);
      }
    }

    // Add all approved files at once
    if (filesToAdd.length > 0) {
      setSelectedFiles(prev => [...prev, ...filesToAdd]);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const filesArray = Array.from(e.dataTransfer.files);
      processFilesWithConfirmation(filesArray);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const filesArray = Array.from(e.target.files);
      processFilesWithConfirmation(filesArray);
    }
  };

  const handleBrowseClick = () => {
    // Reset the file input value to allow selecting the same files again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    fileInputRef.current?.click();
  };

  const handleRemoveFile = (indexToRemove: number) => {
    setSelectedFiles(prev => prev.filter((_, index) => index !== indexToRemove));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveAllFiles = () => {
    setSelectedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    const success = await uploadBlobs(selectedFiles);

    if (success) {
      // Reset form and close dialog
      setSelectedFiles([]);
      onOpenChange(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDialogClose = (open: boolean) => {
    if (!open) {
      // Clear selected files when dialog is closed
      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("knowledgeBase.upload.title")}</DialogTitle>
          <DialogDescription>
            {t("knowledgeBase.upload.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* File Drop Zone */}
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${dragActive
              ? "border-blue-500 bg-blue-50"
              : selectedFiles.length > 0
                ? "border-green-500 bg-green-50"
                : "border-gray-300 hover:border-gray-400"
              }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={handleBrowseClick}
          >
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFileChange}
              accept="*/*"
              multiple
            />

            {selectedFiles.length > 0 ? (
              <div className="space-y-3">
                <File className="w-8 h-8 mx-auto text-green-600" />
                <div>
                  <p className="font-medium text-sm">
                    {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatFileSize(selectedFiles.reduce((total, file) => total + file.size, 0))} total
                  </p>
                </div>

                {/* File List */}
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-white p-2 rounded border text-xs">
                      <div className="flex-1 truncate">
                        <span className="font-medium">{file.name}</span>
                        <span className="text-gray-500 ml-2">({formatFileSize(file.size)})</span>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFile(index);
                        }}
                        className="text-red-500 hover:text-red-700 ml-2"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveAllFiles();
                    }}
                  >
                    <X className="w-4 h-4 mr-1" />
                    {t("knowledgeBase.upload.removeAll")}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBrowseClick();
                    }}
                  >
                    Add More
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload className="w-8 h-8 mx-auto text-gray-400" />
                <div>
                  <p className="text-sm font-medium">
                    {t("knowledgeBase.upload.dropzone.title")}
                  </p>
                  <p className="text-xs text-gray-500">
                    {t("knowledgeBase.upload.dropzone.subtitle")}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleDialogClose(false)} disabled={uploading}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleUpload}
            disabled={selectedFiles.length === 0 || uploading}
          >
            {uploading
              ? t("knowledgeBase.upload.uploading")
              : selectedFiles.length === 1
                ? t("knowledgeBase.upload.uploadSingle")
                : t("knowledgeBase.upload.uploadMultiple", { count: selectedFiles.length })
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
